import React, { useEffect, useMemo } from "react"

import { useFormContext } from "react-hook-form"
import { usaStates } from "typed-usa-states"

import { LegalGender } from "@common-shared/config/coverage.config.ts"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config.ts"
import { SelectElement } from "@front-shared/elements/select.element.tsx"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types.ts"

import { CHECKER_CONFIG } from "../../../../config/checker.config"
import { COVERAGE_CONFIG } from "../../../../config/coverage.config"
import type { IDynamicCheckerFormInputData } from "../../../../types/data.types.ts"
import { MemberIdInput } from "./memberId.input"
import { StateInput } from "./state.input"

export const MedicaidStateMemberIdGenderInput: React.FunctionComponent<
    IMedicaidStateMemberIdGenderInputProps
> = props => {
    const {
        getValues,
        formState: { errors },
        register,
        setValue,
        trigger,
        watch,
    } = useFormContext<IDynamicCheckerFormInputData>()

    const dataWatcher = watch()

    // On state / gender / checkbox change trigger validation of member ID if now it has errors
    useEffect(() => {
        if (!errors.memberId && !errors.memberGender) {
            return
        }

        trigger("memberId")
        trigger("memberGender")
    }, [
        dataWatcher.state,
        dataWatcher.memberGender,
        dataWatcher.memberIdNotAvailable,
    ])

    const renderedMemberGenderOptions = useMemo<ISelectRenderedOption[]>(() => {
        return Object.values(LegalGender).map(value => {
            return {
                value,
                displayValue: CHECKER_CONFIG.genderMapping[value],
            }
        })
    }, [])

    const defaultMemberGender = useMemo<LegalGender | undefined>(() => {
        return dataWatcher.memberGender || props.formDefaultValues?.memberGender
    }, [dataWatcher.memberGender, props.formDefaultValues?.memberGender])

    // For selects with external value prop need to set value manually in form
    useEffect(() => {
        defaultMemberGender && setValue("memberGender", defaultMemberGender)
    }, [defaultMemberGender])

    // Check if state requires member ID
    const isMemberIdRequiredState = useMemo<boolean>(() => {
        return (
            !!dataWatcher.state &&
            COVERAGE_CONFIG.medicaidStatesRequireMemberID.includes(
                dataWatcher.state,
            )
        )
    }, [dataWatcher.state])

    // Check if state requires one of member ID or gender
    const isMemberIdOrGenderRequiredState = useMemo<boolean>(() => {
        return (
            !!dataWatcher.state &&
            COVERAGE_CONFIG.medicaidStatesRequireGenderOrMemberID.includes(
                dataWatcher.state,
            )
        )
    }, [dataWatcher.state])

    const stateName = useMemo<string | undefined>(() => {
        return usaStates.find(state => state.abbreviation === dataWatcher.state)
            ?.name
    }, [dataWatcher.state])

    // Broken out from memberGenderValidation so that we can use it in the label
    const getMemberGenderRequired = () => {
        // Using getValues because watch values are not accessible in validation functions
        const memberGender = getValues("memberGender")
        const memberIdNotAvailable = getValues("memberIdNotAvailable")
        const memberId = getValues("memberId")

        return (
            isMemberIdOrGenderRequiredState &&
            (memberIdNotAvailable || (!memberId && memberGender))
        )
    }

    const memberGenderValidation = () => {
        // Using getValues because watch values are not accessible in validation functions
        const memberGender = getValues("memberGender")

        const isRequired = getMemberGenderRequired()

        return isRequired && !memberGender
            ? FRONT_VALIDATION_CONFIG.required
            : true
    }

    const memberIdValidation = () => {
        // Using getValues because watch values are not accessible in validation functions
        const memberId = getValues("memberId")
        const memberIdNotAvailable = getValues("memberIdNotAvailable")
        const memberGender = getValues("memberGender")

        const isRequired =
            isMemberIdRequiredState ||
            (isMemberIdOrGenderRequiredState &&
                !memberIdNotAvailable &&
                !memberGender)

        return isRequired && !memberId ? FRONT_VALIDATION_CONFIG.required : true
    }

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <div className="flex md:block">
                <StateInput
                    className="
                        mr-[24px] flex-1 flex-shrink-0
                        md:mr-[0px]
                    "
                    defaultValue={props.formDefaultValues?.state}
                    excludeStates={COVERAGE_CONFIG.medicaidStatesNotSupported}
                    disabled={props.disabled}
                />

                <div className="flex-1" />
            </div>

            {dataWatcher.state ? (
                <div className="flex mt-[40px] md:block">
                    <MemberIdInput
                        className="flex-1 mr-[24px] md:mr-[0px]"
                        label={`Member ID${
                            // This condition is deliberately slightly different from the validation condition
                            // In certain cases it can be confusing to show "(optional)", even if we're not validating
                            isMemberIdRequiredState ||
                            (isMemberIdOrGenderRequiredState &&
                                (dataWatcher.memberIdNotAvailable ||
                                    !dataWatcher.memberGender ||
                                    dataWatcher.memberId))
                                ? ""
                                : " (optional)"
                        }`}
                        additionalValidations={{
                            validate: memberIdValidation,
                        }}
                        disabled={
                            props.disabled || !!dataWatcher.memberIdNotAvailable
                        }
                        formDefaultValue={props.formDefaultValues?.memberId}
                        withNotAvailableCheckbox={
                            !props.disabled && isMemberIdOrGenderRequiredState
                        }
                        notAvailableDisabled={
                            props.disabled || !!dataWatcher.memberId
                        }
                        checkboxTooltip={
                            stateName
                                ? `For ${stateName}, Member Gender must be filled if Member ID is unavailable`
                                : undefined
                        }
                        isLabelStatic
                        forceHighlightOnError
                        displayErrorsBelowCheckbox
                    />

                    <SelectElement
                        className={`
                            flex-1 flex-shrink-0
                            md:mr-[0px] ${isMemberIdOrGenderRequiredState ? "md:mt-[36px]" : "md:mt-[40px]"}
                        `}
                        label={`Member Gender${
                            getMemberGenderRequired() ? "" : " (optional)"
                        }`}
                        placeholder="Select one"
                        name="memberGender"
                        allowClear
                        shouldValidate
                        disabled={props.disabled}
                        validations={{
                            validate: memberGenderValidation,
                        }}
                        externalValue={
                            dataWatcher.memberGender
                                ? renderedMemberGenderOptions.find(
                                      item =>
                                          item.value ===
                                          dataWatcher.memberGender,
                                  )?.displayValue
                                : undefined
                        }
                        reactHookFormRegister={register as any}
                        reactHookFormSet={setValue as any}
                        reactHookFormErrors={errors}
                        renderedOptions={[
                            ...(dataWatcher.memberGender
                                ? [
                                      {
                                          value: undefined,
                                          displayValue: "None",
                                      },
                                  ]
                                : []),

                            ...renderedMemberGenderOptions,
                        ]}
                    />
                </div>
            ) : null}
        </div>
    )
}

interface IMedicaidStateMemberIdGenderInputProps {
    className?: string
    formDefaultValues?: IDynamicCheckerFormInputData
    disabled?: boolean
}
